import React, { useMemo, useRef, useState } from 'react'
import { useTheme } from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { GridRow } from 'components/StaticGrid'
import {
  Wrapper,
  Header,
  BackgroundLayer,
  ReleaseInfoFluidContainer,
  StackedScrollableContent,
  StackedSection
} from '../ReleaseInformation.styles'
import { useTranslation } from 'react-i18next'
import { Tonality } from '@dsny/dsny-component-library'
import ReleaseInformationTabs from '../components/Tabs/ReleaseInformationTabs'
import {
  useMobileMediaQuery,
  useTabletAndAboveMediaQuery
} from 'utils/hooks/useMediaQueries'
import Breadcrumb from 'components/Breadcrumb/Breadcrumb'
import GlobalActionMenu from 'components/GlobalActionMenu/GlobalActionMenu'
import PromoDetailsNew from '../components/PromoDetails/PromoDetailsNew'
import ReleaseDetailsNew from '../components/ReleaseDetails/ReleaseDetailsNew'
import HamburguerMenu from 'components/HamburguerMenu/HamburguerMenu'
import { Arrowback } from '@dsny/dsny-component-library/icons'
import ReleaseCoverArtModal from '../components/Modals/ReleaseCoverArtModal'
import BackToTopButton from '../components/BackToTopButton/BackToTopButton'
import { hasPromoEmailContent } from '../utils'
import useReleaseInformationResponsiveBreakpointsV2 from '../hooks/useReleaseInformationResponsiveBreakpointsV2'

const ReleaseInformationColumnLayout = ({
  release,
  setRelease,
  loading,
  handleDownload,
  layoutType = 'column'
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMobileMediaQuery()
  const isTabletAndAbove = useTabletAndAboveMediaQuery()
  const responsiveBreakpointsV2 = useReleaseInformationResponsiveBreakpointsV2()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const promoTitleRef = useRef(null)
  const showPromoSection = useMemo(
    () => hasPromoEmailContent(release?.promoinfo),
    [release?.promoinfo]
  )

  const generateLabels = (pathnames) => {
    const labels = []
    const mapping = {
      releases: 'Releases'
    }

    for (let index = 0; index < pathnames.length; index++) {
      const segment = pathnames[index]

      if (segment === 'releaseInfo') {
        continue
      }

      if (index === 2 && !isNaN(segment)) {
        labels.push({
          title: `Release Information - ${release?.title || ''}`,
          path: `releaseInfo/${segment}`
        })
        continue
      }

      labels.push({ title: mapping[segment] || segment, path: segment })
    }

    return labels
  }

  return (
    <Wrapper
      $responsiveBreakpointsV2={responsiveBreakpointsV2}
      data-cy="release-detail-root"
      data-layout={layoutType}
    >
      <Header
        $responsiveBreakpointsV2={responsiveBreakpointsV2}
        style={{ padding: isMobile ? '16px' : '16px 24px 0' }}
      >
        <BackgroundLayer
          $responsiveBreakpointsV2={responsiveBreakpointsV2}
          style={{ backgroundImage: `url(${release?.coverart})` }}
        />
        <ReleaseInfoFluidContainer>
          <GridRow
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginBottom: '32px'
            }}
          >
            <HamburguerMenu style={{ marginRight: 20 }} />
            {isTabletAndAbove && <Breadcrumb generateLabels={generateLabels} />}
            <GlobalActionMenu style={{ marginLeft: 'auto' }} />
          </GridRow>

          {isMobile && (
            <GridRow
              style={{
                padding: '16px 0',
                marginBottom: '12px'
              }}
            >
              <Arrowback
                width={18}
                style={{ marginRight: '16px' }}
                color={theme.colors.neutralW80}
                onClick={() => navigate('/releases')}
              />
              <Breadcrumb generateLabels={generateLabels} />
            </GridRow>
          )}

          <GridRow style={{ position: 'relative' }}>
            <ReleaseDetailsNew
              release={release}
              setIsModalOpen={setIsModalOpen}
              loading={loading}
              handleDownload={handleDownload}
              setRelease={setRelease}
            />
          </GridRow>
        </ReleaseInfoFluidContainer>
      </Header>

      <StackedScrollableContent
        $responsiveBreakpointsV2={responsiveBreakpointsV2}
      >
        <StackedSection id="tabs-section">
          <ReleaseInformationTabs release={release} />
        </StackedSection>
        {showPromoSection && (
          <StackedSection
            id="promo-section"
            style={{
              paddingBottom: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 32
            }}
          >
            <div ref={promoTitleRef}>
              <Tonality.UIHeading
                headingStyle="headline5"
                color={theme.colors.neutralW100}
                style={{ fontSize: 20 }}
              >
                {t('release-info.promotional-email')}
              </Tonality.UIHeading>
            </div>
            <PromoDetailsNew promoInfo={release?.promoinfo} />
          </StackedSection>
        )}
      </StackedScrollableContent>

      <BackToTopButton promoTitleRef={promoTitleRef} />

      {isModalOpen && (
        <ReleaseCoverArtModal
          coverArt={release?.coverart}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </Wrapper>
  )
}

export default ReleaseInformationColumnLayout
